import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import useEnrolPageProps from "@hooks/useEnrolPageProps";
import usePostCheckoutState from "@hooks/usePostCheckoutState";
import useResetAtomicState from "@hooks/useResetAtomicState";
import useSessionStateSync from "@hooks/useSessionStateSync";

import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtom } from "jotai";

import {
    Button,
    Divider,
    Flex,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useDisclosure
} from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { StateSlicesEnum } from "@ts/enums/StateSlicesEnum";
import { AddonInterface } from "@ts/interfaces/Checkout/AddonInterface";
import { CheckoutStateInterface } from "@ts/interfaces/Misc/state/CheckoutStateInterface";

import declinedPremiumService from "@assets/img/declined-premium-service.svg";

import {
    canAddonSelectionSkipAtom,
    checkoutAtom,
    isNewsletterSubEnabledAtom,
    loadableProductFamiliesAtom,
    pageQueryParamsAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom.ts";

import { dispatchHSEvents } from "@utils/dispatchHSEevents";
import { getTrackingId, sortProducts } from "@utils/utils";

import ContentLayout from "@layouts/ContentLayout";

import { Card } from "@components/Card";
import { Skeleton } from "@components/Skeleton";
import { Text } from "@components/Text";

declare function route(
    name: string,
    params?: string | { [key: string]: string }
): string;

const PremiumServicesContainer = () => {
    // TODO: fix isHighlighted field as it currently denotes non bundled addons

    const { hubspotEventCheckout } = useCheckoutPageProps();
    const { hubspotEventEnrol } = useEnrolPageProps();

    const navigate = useNavigate();

    const [productFamilies] = useAtom(loadableProductFamiliesAtom);
    const [checkout, setCheckout] = useAtom(checkoutAtom);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [userCheckoutStateToken] = useAtom(userCheckoutStateTokenAtom);
    const [canAddonSelectionSkip, setCanAddonSelectionSkip] = useAtom(
        canAddonSelectionSkipAtom
    );
    const [isNewsletterSubEnabled] = useAtom(isNewsletterSubEnabledAtom);

    const [bundledAddonCrossedPrice, setBundledAddonCrossedPrice] = useState(0);
    const [learnMore, setLearnMore] = useState<string[]>([]);
    const [selectedAddon, setSelectedAddon] = useState<AddonInterface | null>(
        checkout.addon
    );
    const [addons, setAddons] = useState<AddonInterface[]>([]);

    const [syncCheckoutSessionState, sessionState] =
        useSessionStateSync<CheckoutStateInterface>();

    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();
    const { checkoutStateMutationState, checkoutStateMutate } =
        usePostCheckoutState(() => {
            navigate(routeWithQueryParams(CheckoutPageRoutesEnum.PAYMENT_PAGE));
        });

    const { resetCheckoutAtomicState } = useResetAtomicState();

    const checkoutPurchasingAddon =
        sessionState.checkout?.checkoutPurchasingAddon ?? null;

    const addonEntitlementsIds =
        sessionState.checkout?.addonEntitlementsIds ?? [];
    /**
     * useEffect to pre-construct the checkout state because parent user is coming directly
     * here from the parents portal and trying purchase an addon.
     */
    useEffect(() => {
        if (productFamilies.state === "hasData") {
            if (checkoutPurchasingAddon) {
                const productFamily =
                    productFamilies.data.find(
                        pF => pF.id === checkoutPurchasingAddon.productFamilyId
                    ) ?? null;

                const grade = productFamily
                    ? productFamily.grades.find(
                          g => g.name === checkoutPurchasingAddon.grade
                      ) ?? null
                    : null;
                const plan = productFamily
                    ? productFamily.plans.find(
                          p => p.id === checkoutPurchasingAddon.planId
                      ) ?? null
                    : null;

                setCheckout(prev => ({
                    ...prev,
                    productFamily,
                    grade,
                    plan
                }));

                /**
                 * We need to prevent users from purchasing an addon that's already been purchased from FE.
                 * So we are filtering out addons based on the entitlement ID of the already purchased addons.
                 */
                const addons = plan?.charges?.addons?.filter(
                    a =>
                        !(a.entitlementsIds ?? []).some(e =>
                            addonEntitlementsIds.includes(e)
                        )
                );

                setAddons(sortProducts<AddonInterface>(addons ?? [], "order"));
            } else {
                setAddons(checkout.plan?.addons || []);
            }
        }
    }, [productFamilies]);

    const isAddonSelected = !!checkout.addon;

    const { isOpen: isOpenStateRegWarning, onClose: onCloseStateRegWarning } =
        useDisclosure({
            defaultIsOpen: pageQueryParams.locationState === "tas"
        });

    const onSelectAddon = (addon: AddonInterface) => {
        dispatchHSEvents(
            "trackPageView",
            CheckoutPageRoutesEnum.PAYMENT_PAGE,
            undefined,
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );

        setCheckout(prev => ({
            ...prev,
            addon: addon.price > 0 ? addon : null,
            nextAttemptPage: CheckoutPagesEnum.PAYMENT_PAGE
        }));
        setSelectedAddon(addon);

        if (addon.price <= 0) {
            setCanAddonSelectionSkip(true);
        } else {
            setCanAddonSelectionSkip(false);
        }
    };

    const onCancel = () => {
        resetCheckoutAtomicState();

        syncCheckoutSessionState(
            StateSlicesEnum.CHECKOUT,
            {
                checkoutRedirectsTo: null,
                checkoutStudentId: null,
                checkoutStudentCBId: null,
                checkoutPurchasingAddon: null,
                addonEntitlementsIds: null,
                hasPendingSubCreated: false,
                checkoutUser: {
                    receiveOfferNews: false,
                    firstName: "",
                    email: "",
                    nextAttemptPage: CheckoutPagesEnum.USER_INFO_PAGE
                }
            },
            sessionState.checkout?.checkoutRedirectsTo ??
                route("parents.manage-programs") // nosonar
        ).catch(e => console.log(e));
    };

    const onGoBack = () => {
        // If the selected product family has one plan, the plan selection page will be skipped from showing.
        if (checkout.productFamily!.plans.length === 1) {
            navigate(
                routeWithQueryParams(
                    CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                )
            );
        } else {
            navigate(
                routeWithQueryParams(CheckoutPageRoutesEnum.PLAN_SELECT_PAGE)
            );
        }
    };

    const onSaveData = () => {
        dispatchHSEvents(
            "trackPageView",
            CheckoutPageRoutesEnum.PAYMENT_PAGE,
            undefined,
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );
        dispatchHSEvents(
            "trackCustomBehavioralEvent",
            undefined,
            {
                checkout_status: "ABANDON",
                email: checkout.user.email,
                checkout_token: userCheckoutStateToken,
                newsletter_subscription: isNewsletterSubEnabled ? "yes" : "no"
            },
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );

        checkoutStateMutate();
    };

    const addonsWithNoPremium = {
        id: "No-premium-services-right-now",
        name: "No thanks, I don't want Premium Services right now",
        priceId: "",
        description:
            "While Premium Services can be added later, adding them now ensures you start your homeschooling journey with the selected benefits enabled.",
        coverImage: declinedPremiumService,
        isHighlighted: false,
        highlightedText: "",
        price: -1,
        isIncluded: false,
        currency: "",
        priceLabel: "",
        billingFrequency: "",
        crossedPriceLabel: "",
        features: [],
        isBulkAddon: false,
        order: 100
    };

    const formattedAddons = useMemo(() => {
        if (addons) {
            const newAddons = [
                ...addons,
                ...((checkoutPurchasingAddon
                    ? []
                    : [addonsWithNoPremium]) as AddonInterface[])
            ];
            const addonIdxToMove = newAddons.findIndex(a => a.isIncluded);
            if (addonIdxToMove !== -1) {
                const [movedAddon] = newAddons.splice(addonIdxToMove, 1);
                newAddons.unshift(movedAddon);
            }
            return newAddons;
        }
        return [];
    }, [addons]);

    const handleLearnMore = (planId: string) => {
        setLearnMore(prevExpandedItems => {
            if (prevExpandedItems.includes(planId)) {
                return prevExpandedItems.filter(id => id !== planId);
            }
            return [...prevExpandedItems, planId];
        });
    };

    useEffect(() => {
        if (addons && addons.length > 1) {
            const bundledPrice = addons
                .filter(addon => !addon.isBulkAddon)
                .reduce((sum, addon) => {
                    return sum + addon.price;
                }, 0);
            setBundledAddonCrossedPrice(bundledPrice);
        }
    }, [addons]);

    /**
     * "useEffect" hook to set the query parameters dynamically based on the selected product.
     */
    useEffect(() => {
        if (checkoutStateMutationState.isSuccess) {
            if (!checkoutPurchasingAddon)
                // Update the related query parameters with the selected addon.
                onSetupQueryParams({
                    ...pageQueryParams,
                    addon: checkout.addon ? checkout.addon.id : null
                });
        }
    }, [checkout, checkoutStateMutationState]);

    useEffect(() => {
        if (checkout.addon) {
            setSelectedAddon(checkout.addon);
        } else {
            if (canAddonSelectionSkip) setSelectedAddon(addonsWithNoPremium);
        }
    }, [checkout]);

    const getPremiumServiceTrackingId = (service: AddonInterface) => {
        const updatedServiceName = service.name;
        if (service.id === "No-premium-services-right-now") {
            return service.id;
        }
        return updatedServiceName.replace(/\s/g, "");
    };

    return (
        <ContentLayout
            className={ClassesEnum.PremiumServiceContainer}
            title={
                checkoutPurchasingAddon
                    ? "Select a premium service"
                    : "Add a premium service to simplify registration & reporting"
            }
            footerActionsProps={{
                canGoNext: isAddonSelected || canAddonSelectionSkip,
                hasCancelButton: !!checkoutPurchasingAddon,
                hasBackButton: !checkoutPurchasingAddon,
                onCancel,
                onGoBack,
                isNextBtnLoading: checkoutStateMutationState.isPending,
                onGoNext: onSaveData,
                btnClassHierarchy: {
                    next: [
                        ClassesEnum.PremiumServiceContainer,
                        ClassesEnum.Button
                    ],
                    back: [
                        ClassesEnum.PremiumServiceContainer,
                        ClassesEnum.Button
                    ]
                },
                btnActions: {
                    next: "goNextFromAddonSelectPage",
                    back: "goBackFromAddonSelectPage"
                }
            }}
            containerWidth={["95%", null, null, null, 950]}
            step={3}
        >
            <Flex direction="column" gap={8} align="center">
                {productFamilies.state === "hasData" &&
                    formattedAddons!.map(a => {
                        return (
                            <Card<AddonInterface>
                                classHierarchy={[
                                    ClassesEnum.PremiumServiceContainer,
                                    ClassesEnum.Card
                                ]}
                                action={`selectAddon-${getPremiumServiceTrackingId(
                                    a
                                )}`}
                                key={a.id}
                                item={a}
                                isHighlighted={!!a.highlightedText}
                                highlightedText={a.highlightedText}
                                isActive={a.id === selectedAddon?.id}
                                onClick={
                                    !a.isIncluded ? onSelectAddon : undefined
                                }
                                isClickable={!a.isIncluded}
                                width={758}
                                height="auto"
                                highlightedTextAlign="end"
                                cardBorderRadius="24px"
                                bgColor={
                                    a.isIncluded
                                        ? "var(--euka-colors-gray-100)"
                                        : "white"
                                }
                            >
                                <Flex
                                    gap={4}
                                    h="full"
                                    w="full"
                                    id={`addon-${a.id}`}
                                >
                                    <Image
                                        src={a.coverImage}
                                        alt={a.name}
                                        pointerEvents="none"
                                        userSelect="none"
                                        marginBottom="auto"
                                        display={["none", null, "inline-block"]}
                                        order={-1}
                                    />
                                    {a.id !==
                                        "No-premium-services-right-now" && (
                                        <>
                                            <Flex
                                                direction="column"
                                                pt={0.5}
                                                pb={0.5}
                                            >
                                                <Divider
                                                    color="gray.300"
                                                    border="1px"
                                                    orientation="vertical"
                                                    display={{
                                                        base: "none",
                                                        md: "block"
                                                    }}
                                                />
                                            </Flex>
                                            <Flex
                                                direction="column"
                                                justify={[
                                                    "start",
                                                    null,
                                                    "center"
                                                ]}
                                                ml="auto"
                                                gap={1}
                                                whiteSpace="nowrap"
                                                textAlign="end"
                                            >
                                                {!a.isHighlighted &&
                                                    bundledAddonCrossedPrice >
                                                        0 && (
                                                        <Text
                                                            fontSize={14}
                                                            type="subHeading"
                                                            color="gray.500"
                                                            fontWeight="bold"
                                                            textDecoration="line-through"
                                                            fontFamily="inter, sans-serif"
                                                            className={
                                                                ClassesEnum.Text
                                                            }
                                                            data-tracking-id={getTrackingId(
                                                                [
                                                                    ClassesEnum.PremiumServiceContainer,
                                                                    ClassesEnum.Card,
                                                                    ClassesEnum.Text
                                                                ],
                                                                `bundledAddonCrossedPrice-${getPremiumServiceTrackingId(
                                                                    a
                                                                )}`
                                                            )}
                                                        >
                                                            $
                                                            {
                                                                bundledAddonCrossedPrice
                                                            }{" "}
                                                            AUD
                                                        </Text>
                                                    )}
                                                {a.isIncluded ? (
                                                    <Text
                                                        fontSize={20}
                                                        fontWeight={600}
                                                        fontFamily="inter, sans-serif"
                                                        lineHeight="30px"
                                                        className={
                                                            ClassesEnum.Text
                                                        }
                                                        data-tracking-id={getTrackingId(
                                                            [
                                                                ClassesEnum.PremiumServiceContainer,
                                                                ClassesEnum.Card,
                                                                ClassesEnum.Text
                                                            ],
                                                            `included-${getPremiumServiceTrackingId(
                                                                a
                                                            )}`
                                                        )}
                                                    >
                                                        Included
                                                    </Text>
                                                ) : (
                                                    <>
                                                        <Text
                                                            fontSize={20}
                                                            fontWeight={600}
                                                            fontFamily="inter, sans-serif"
                                                            lineHeight="30px"
                                                            className={
                                                                ClassesEnum.Text
                                                            }
                                                            data-tracking-id={getTrackingId(
                                                                [
                                                                    ClassesEnum.PremiumServiceContainer,
                                                                    ClassesEnum.Card,
                                                                    ClassesEnum.Text
                                                                ],
                                                                `price-${getPremiumServiceTrackingId(
                                                                    a
                                                                )}`
                                                            )}
                                                        >
                                                            ${a.priceLabel}
                                                        </Text>
                                                        <Text
                                                            fontFamily="ballinger, sans-serif"
                                                            fontSize={16}
                                                            className={
                                                                ClassesEnum.Text
                                                            }
                                                            data-tracking-id={getTrackingId(
                                                                [
                                                                    ClassesEnum.PremiumServiceContainer,
                                                                    ClassesEnum.Card,
                                                                    ClassesEnum.Text
                                                                ],
                                                                `billingFrequency-${getPremiumServiceTrackingId(
                                                                    a
                                                                )}`
                                                            )}
                                                        >
                                                            {a.billingFrequency}
                                                        </Text>
                                                    </>
                                                )}
                                            </Flex>
                                        </>
                                    )}
                                    <Flex direction="column" gap={3} order={-1}>
                                        <Text type="subHeading">{a.name}</Text>
                                        <Text
                                            type="normal"
                                            display={[
                                                learnMore.includes(a.id)
                                                    ? "block"
                                                    : "none",
                                                null,
                                                "inline-block"
                                            ]}
                                            fontSize={14}
                                            className={ClassesEnum.Text}
                                            data-tracking-id={getTrackingId(
                                                [
                                                    ClassesEnum.PremiumServiceContainer,
                                                    ClassesEnum.Card,
                                                    ClassesEnum.Text
                                                ],
                                                `description-${getPremiumServiceTrackingId(
                                                    a
                                                )}`
                                            )}
                                        >
                                            {a.description}
                                        </Text>
                                    </Flex>
                                </Flex>
                                <Button
                                    display={["flex", null, "none"]}
                                    fontFamily="ballinger, sans-serif"
                                    fontSize={14}
                                    fontWeight={400}
                                    onClick={() => handleLearnMore(a.id)}
                                    variant="link"
                                    p={0}
                                    alignSelf="start"
                                    gap={1}
                                    mt={4}
                                    lineHeight="30px"
                                    _hover={{
                                        textDecoration: "none"
                                    }}
                                    className={ClassesEnum.Button}
                                    data-tracking-id={getTrackingId(
                                        [
                                            ClassesEnum.PremiumServiceContainer,
                                            ClassesEnum.Card,
                                            ClassesEnum.Button
                                        ],
                                        `learnMore-${getPremiumServiceTrackingId(
                                            a
                                        )}`
                                    )}
                                >
                                    <span>
                                        {learnMore.includes(a.id)
                                            ? "Hide"
                                            : "Learn more"}
                                    </span>

                                    {!learnMore.includes(a.id) && (
                                        <FontAwesomeIcon
                                            icon={faChevronDown}
                                            color="var(--euka-colors-royalBlue-500)"
                                            fontSize={14}
                                        />
                                    )}
                                    {learnMore.includes(a.id) && (
                                        <FontAwesomeIcon
                                            icon={faChevronUp}
                                            color="var(--euka-colors-royalBlue-500)"
                                            fontSize={14}
                                        />
                                    )}
                                </Button>
                            </Card>
                        );
                    })}
                {productFamilies.state === "loading" && <Skeleton />}
            </Flex>

            <Modal
                isOpen={isOpenStateRegWarning}
                onClose={onCloseStateRegWarning}
                size="xxl"
                isCentered
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        Premium Services for families in Tasmania
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody>
                        <Text>
                            Looks like you're in Tasmania! Please note that
                            while our Premium Services support your registration
                            and reporting needs, any documents created by our
                            service may not receive approval from the Office of
                            the Education Registrar in Tasmania if submitted
                            directly. They can still serve as a valuable guide
                            for your own documentation.
                        </Text>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            colorScheme="ebonyClay"
                            onClick={onCloseStateRegWarning}
                        >
                            Got it
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </ContentLayout>
    );
};

export default PremiumServicesContainer;
